import { useEffect, useState, useCallback } from 'react'
import PhenotypeFilter from 'src/views/VariantsView/Toolbar/FiltersPanelMenu/PhenotypeFilter'
import useDiseaseSelection from 'src/hooks/useDiseaseSelection'
import { Box, Typography } from '@material-ui/core'
import diseaseService from 'src/api/filters/diseaseService'

/**
 * A wrapper component for PhenotypeFilter to be used in AnnotationDialog and later
 * in FiltersPanelMenu
 */
const DiseaseFilterWrapper = ({
  value = [],
  onChange,
  stringOutput = false,
  arrayOutput = false,
  label = 'Diseases',
  showInheritance = false,
  fileId = null, // This is used to whether we are in the backend or local context
}) => {
  const handleDiseasesChange = useCallback(
    (updatedDiseases) => {
      if (onChange) {
        // We don't need these anymore but kept for reference
        // if (arrayOutput) {
        //   // Transform the disease objects into the format needed for the backend
        //   const transformedDiseases = {
        //     diseases: updatedDiseases.map((d) => d.disease),
        //     diseaseIds: updatedDiseases.map((d) => d.omim || ''),
        //     diseaseKeys: updatedDiseases.map((d) => d.key || ''),
        //   }
        //   onChange(transformedDiseases)
        // } else if (stringOutput) {
        //   // Convert to string
        //   const asString = updatedDiseases.length > 0 ? updatedDiseases.map((d) => d.disease).join(', ') : ''
        //   onChange(asString)
        // } else {
        //   // Pass through the disease objects directly
        onChange(updatedDiseases)
        // }
      }
    },
    [onChange],
  )

  const { diseases, setDiseases, inheritanceModes, setInheritanceModes, setDiseasesFromString } = useDiseaseSelection(
    [],
    handleDiseasesChange,
  )

  useEffect(() => {
    if (typeof value === 'string' && value !== diseases.map((d) => d.disease).join(', ')) {
      setDiseasesFromString(value)
    } else if (Array.isArray(value)) {
      const formattedValue = value.map((disease) => ({
        key: disease.key || 0,
        disease: disease.disease || '',
        description: disease.description || '',
        inheritance: disease.inheritance || '',
        omim: disease.omim || '',
      }))
      if (JSON.stringify(formattedValue) !== JSON.stringify(diseases)) {
        setDiseases(formattedValue)
      }
    }
  }, [value, diseases, setDiseasesFromString, setDiseases])

  // Handle disease deletion
  const handleDeleteDisease = useCallback(
    async (diseaseKey) => {
      if (!fileId) {
        // Local-only context: just update the state
        const newDiseases = diseases.filter((d) => d.key !== diseaseKey)
        setDiseases(newDiseases)
        return
      }

      try {
        // Backend context: update both state and backend
        const newDiseases = diseases.filter((d) => d.key !== diseaseKey)
        const diseaseKeys = newDiseases.map((d) => d.key)

        await diseaseService.updateDiseaseList(fileId, diseaseKeys)

        setDiseases(newDiseases)
      } catch (error) {
        console.error('Error deleting disease:', error)
      }
    },
    [diseases, setDiseases, fileId],
  )

  const handleClearAll = useCallback(async () => {
    if (!fileId) {
      // Local-only context: just clear the state
      setDiseases([])
      return
    }

    try {
      // Backend context: clear both state and backend
      await diseaseService.deleteDisease(fileId)
      setDiseases([])
    } catch (error) {
      console.error('Error clearing diseases:', error)
    }
  }, [setDiseases, fileId])

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {label}
        </Typography>
      )}
      <PhenotypeFilter
        phenotypes={diseases}
        setPhenotypes={setDiseases}
        inheritanceModes={inheritanceModes}
        setInheritanceModes={setInheritanceModes}
        showInheritance={showInheritance}
        onDeleteDisease={handleDeleteDisease}
        onClearAll={handleClearAll}
      />
    </Box>
  )
}

export default DiseaseFilterWrapper
